import { RecoProductBasedRecommendationsItemData } from '@cube/tracking-library-api';
import { debounceFn } from '../../../scripts/utils/debounce.js';
import { trackRecoClick, trackRecoView } from '../../../scripts/service/cube-tracking-layer.js';

export default class RecoTracking {
  private readonly container: HTMLElement;

  private readonly slideSelector: string;

  private readonly staticTrackingData: object = {};

  private intersectionObserver: IntersectionObserver | null = null;

  private elementsCache: Record<
    number,
    {
      visible: boolean;
      element: HTMLElement;
      position: number;
    }
  > = {};

  private trackingSent: Record<number, boolean> = {};

  declare private sendTrackingDebounced: () => void;

  constructor(container: HTMLElement, slideSelector: string, staticTrackingData: object = {}) {
    this.container = container;
    this.slideSelector = slideSelector;
    this.staticTrackingData = staticTrackingData;

    this.sendTrackingDebounced = debounceFn(this.sendViewTracking.bind(this), 500);
    this.onSlideClick = this.onSlideClick.bind(this);
  }

  observe(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const position = RecoTracking.getPosition(e.target as HTMLElement);
          this.elementsCache[position] = {
            visible: e.isIntersecting,
            element: e.target as HTMLElement,
            position,
          };
          this.sendTrackingDebounced();
        });
      },
      { threshold: [0.9] },
    );
    this.container.querySelectorAll(this.slideSelector).forEach((slide) => {
      this.intersectionObserver?.observe(slide);
    });
    this.container.querySelectorAll(this.slideSelector).forEach((slide) => {
      slide.addEventListener('click', this.onSlideClick);
    });
  }

  unobserve(): void {
    this.intersectionObserver?.disconnect();
    this.container.querySelectorAll(this.slideSelector).forEach((slide) => {
      slide.removeEventListener('click', this.onSlideClick);
    });
    this.elementsCache = {};
    this.trackingSent = {};
  }

  private onSlideClick(event: Event): void {
    const target = event.target as HTMLElement;
    const slide = target.closest(this.slideSelector) as HTMLElement;
    const itemTrackingData = RecoTracking.getTrackingData(slide) as RecoProductBasedRecommendationsItemData;
    if (itemTrackingData === null) {
      return;
    }
    trackRecoClick({
      ...this.staticTrackingData,
      items: [itemTrackingData],
      event_data: {
        is_interaction_event: true,
      },
    });
  }

  private static getPosition(slide: HTMLElement): number {
    const trackingDataElement: HTMLElement | null = RecoTracking.getTrackingDataElement(slide);
    if (!trackingDataElement) {
      return -1;
    }
    return parseInt(trackingDataElement.dataset.position ?? '0', 10);
  }

  private static getTrackingDataElement(element: HTMLElement): HTMLElement | null {
    return element.dataset.trackingData ? element : element.querySelector('[data-tracking-data]');
  }

  private static getTrackingData(slide: HTMLElement): Partial<RecoProductBasedRecommendationsItemData> | null {
    const trackingDataElement = RecoTracking.getTrackingDataElement(slide);
    if (!trackingDataElement) {
      return null;
    }
    const trackingData = JSON.parse(trackingDataElement.dataset.trackingData ?? '{}');
    return {
      ...trackingData,
      position: RecoTracking.getPosition(slide) + 1,
    };
  }

  private sendViewTracking(): void {
    const visibleElementsAndUntracked = Object.values(this.elementsCache)
      .filter((v) => v.visible)
      .filter((v) => !this.trackingSent[v.position]);
    visibleElementsAndUntracked.forEach((v) => {
      this.trackingSent[v.position] = true;
    });

    const items = visibleElementsAndUntracked
      .map((v) => RecoTracking.getTrackingData(v.element))
      .filter((v) => v !== null) as RecoProductBasedRecommendationsItemData[];

    if (items.length > 0) {
      trackRecoView({
        ...this.staticTrackingData,
        items,
      });
    }
  }
}
